import { Box, useBreakpointValue } from "@chakra-ui/react";
import { type KeyboardEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { DatePickerGlobalStyles } from "./DatePickerGlobalStyles";
import { DatePickerHeader } from "./DatePickerHeader";
import {
  PORTAL_ID,
  PickerCustomInput,
  datepickerLocaleMap,
  parsePickerIsoDate,
  useDatepickerPortal,
  usePickerLanguage,
  SelectedDateAnnouncement,
} from "./pickerInternals";

interface ThemedDateTimePickerProps {
  id?: string;
  /**
   * Accessible name for the underlying input. Required when the caller renders no
   * visible <label htmlFor> (e.g. the milestone timeline hides the label), otherwise
   * screen-reader users land on an unnamed date field.
   */
  ariaLabel?: string;
  defaultDate?: string;
  date?: string | null;
  onChange: (isoDateTime?: string) => Promise<void> | void;
  onBlur?: () => Promise<void> | void;
  isDisabled?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  minDate?: Date;
  laterThan?: Date;
  maxDate?: Date;
  timeIntervals?: number;
  timeCaption?: string;
  /**
   * Local-time fallback applied when the user picks a date in the calendar
   * without choosing a time (react-datepicker emits 00:00:00.000 in that case).
   * Tender deadlines pass {hours: 23, minutes: 59} so picking just a date
   * means "end of that day". Explicit time-list selections are preserved.
   */
  defaultTime?: { hours: number; minutes: number };
  /**
   * Render the calendar directly without the text input field — useful when the
   * caller already shows the chosen date elsewhere and wants the picker to skip
   * the input step (e.g. inside a Popover triggered by an icon button).
   */
  inline?: boolean;
  isClearable?: boolean;
}

const withDefaultTime = (
  d: Date,
  defaultTime: { hours: number; minutes: number },
): Date => {
  if (d.getHours() !== 0 || d.getMinutes() !== 0 || d.getSeconds() !== 0) {
    return d;
  }
  const out = new Date(d);
  out.setHours(defaultTime.hours, defaultTime.minutes, 0, 0);
  return out;
};

const firstSlotAfter = (moment: Date, intervalMinutes: number): Date => {
  const slot = new Date(moment);
  slot.setSeconds(0, 0);
  const minutes = slot.getMinutes();
  slot.setMinutes(minutes - (minutes % intervalMinutes) + intervalMinutes);
  return slot;
};

export const ThemedDateTimePicker = ({
  id,
  ariaLabel,
  defaultDate,
  date,
  onChange,
  onBlur,
  isDisabled,
  autoFocus = false,
  placeholder,
  minDate,
  laterThan,
  maxDate,
  timeIntervals = 30,
  timeCaption,
  defaultTime,
  inline = false,
  isClearable = false,
}: ThemedDateTimePickerProps) => {
  const { i18n, t } = useTranslation();
  const intlLocale = datepickerLocaleMap[i18n.language] ?? "nb-NO";
  const resolvedTimeCaption = timeCaption ?? t("Time") ?? "Time";
  const [internalDate, setInternalDate] = useState<Date | null>(
    parsePickerIsoDate(defaultDate, { warnOnInvalid: true }),
  );
  const selected =
    date !== undefined ? parsePickerIsoDate(date, { warnOnInvalid: true }) : internalDate;
  const pickerRef = useRef<DatePicker>(null);

  const pickerLanguage = usePickerLanguage(true);
  useDatepickerPortal();
  const onPhone = (useBreakpointValue({ base: true, sm: false }, { ssr: false }) ?? false) && !inline;

  const handleChange = (d: Date | null) => {
    const withTime = d && defaultTime ? withDefaultTime(d, defaultTime) : d;
    const normalized =
      withTime && laterThan && withTime <= laterThan ? firstSlotAfter(laterThan, timeIntervals) : withTime;
    setInternalDate(normalized);
    onChange(normalized ? normalized.toISOString() : undefined);
    // showTimeSelect keeps the popper open by default (expecting a time pick),
    // but when a defaultTime is supplied that pick is implicit — close so the
    // user sees inline validation that lands below the field. When no
    // defaultTime is configured, leave the popper open so the user can pick
    // their own time.
    if (normalized && defaultTime) pickerRef.current?.setOpen(false);
  };

  const clearOnDeleteKey = (event: KeyboardEvent<HTMLElement>) => {
    if (!isClearable || !selected || (event.key !== "Backspace" && event.key !== "Delete")) return;
    event.preventDefault();
    handleChange(null);
  };

  return (
    <Box
      sx={{ ".react-datepicker-wrapper": { maxWidth: { sm: "full", md: "60" } } }}
      className={inline ? "themed-datepicker-inline" : undefined}
    >
      <DatePickerGlobalStyles />
      <DatePicker
        ref={pickerRef}
        id={id}
        selected={selected}
        onChange={handleChange}
        onCalendarClose={onBlur}
        autoFocus={autoFocus}
        showTimeSelect
        timeIntervals={timeIntervals}
        timeCaption={resolvedTimeCaption}
        dateFormat="dd.MM.yyyy HH:mm"
        placeholderText={placeholder ?? "dd.mm.yyyy tt:mm"}
        customInput={
          <PickerCustomInput
            aria-label={ariaLabel}
            onClear={isClearable ? () => handleChange(null) : undefined}
            clearLabel={t("Clear date") ?? undefined}
          />
        }
        disabled={isDisabled}
        onKeyDown={clearOnDeleteKey}
        minDate={minDate ?? laterThan ?? new Date()}
        filterTime={laterThan ? (time) => time > laterThan : undefined}
        maxDate={maxDate}
        showPopperArrow={false}
        popperPlacement="bottom-start"
        portalId={inline ? undefined : PORTAL_ID}
        withPortal={onPhone}
        onCalendarOpen={onPhone ? centreChosenTime : undefined}
        inline={inline}
        renderCustomHeader={(props) => <DatePickerHeader {...props} locale={intlLocale} />}
        {...pickerLanguage}
      />
      <SelectedDateAnnouncement selected={selected} withTime locale={intlLocale} />
    </Box>
  );
};

// react-datepicker re-centres the time list in its own frame against the calendar's height; the second frame wins.
const centreChosenTime = () =>
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      const list = document.querySelector<HTMLElement>(`#${PORTAL_ID} .react-datepicker__time-list`);
      const chosen = list?.querySelector<HTMLElement>(".react-datepicker__time-list-item--selected");
      if (list && chosen) list.scrollTop = chosen.offsetTop - (list.clientHeight - chosen.clientHeight) / 2;
    }),
  );
